import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { FurnitureCategory, RoomType } from "../../types/C1_ModelTypes";
import { C1_FurnitureSvg, C1_FurnitureUprightContent } from "../C1_FurnitureSvg";

type EquipmentSymbolProps = {
  width?: number;
  height?: number;
  detailScale?: number;
  strokeWidth?: number;
  fillColor?: string;
  strokeColor?: string;
};

function OutdoorHeatPumpSymbol({
  width = 22, height = 15.5, detailScale = 1, strokeWidth = 0.85,
  fillColor = "none", strokeColor = "currentColor",
  lamellaSpacing = 22 * 0.05 / 0.9,
}: EquipmentSymbolProps & { lamellaSpacing?: number }) {
  const u = Math.min(detailScale, width / 22, height / 15.5);
  const bodyHeight = height - 3 * u;
  const bodyBottom = height - 2.5 * u;
  const dividerX = width - 8 * u;
  const lamellaCount = Math.max(0, Math.floor((dividerX - 6 * u) / lamellaSpacing) + 1);
  return (
    <g fill="none" stroke={strokeColor} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <rect x={u} y={0.5 * u} width={width - 2 * u} height={bodyHeight} rx={0.8 * u} fill={fillColor} />
      {[3.3 * u, width - 5.7 * u].map(x => (
        <path key={x} d={`M${x} ${bodyBottom}v${1.5 * u}h${2.4 * u}v${-1.5 * u}`} />
      ))}
      <path d={`M${dividerX} ${2 * u}V${bodyBottom - 1.5 * u}`} />
      {Array.from({ length: lamellaCount }, (_, index) => (
        <path key={`vertical-${index}`} d={`M${3.5 * u + lamellaSpacing * index} ${2 * u}V${bodyBottom - 1.5 * u}`} />
      ))}
      {[0, 1, 2, 3, 4, 5].map(index => (
        <path key={`horizontal-${index}`} d={`M${dividerX + 1.7 * u} ${2.8 * u + (bodyHeight - 4.9 * u) * index / 5}H${width - 3 * u}`} />
      ))}
    </g>
  );
}

function WallboxSymbol({
  width = 22, height = 19.5, detailScale = 1, strokeWidth = 0.85,
  fillColor = "none", strokeColor = "currentColor",
}: EquipmentSymbolProps) {
  const u = Math.min(detailScale, width / 22, height / 19.5);
  const bodyWidth = width - 11 * u;
  const bodyHeight = height - 4.5 * u;
  const bodyRight = u + bodyWidth;
  const centerX = u + bodyWidth / 2;
  const cableY = u + bodyHeight * 0.48;
  const cableBottom = height - 1.2 * u;
  const plugX = bodyRight + 5.4 * u;
  const plugY = cableY - 2.5 * u;
  return (
    <g fill="none" stroke={strokeColor} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <rect x={u} y={u} width={bodyWidth} height={bodyHeight} rx={1.5 * u} fill={fillColor} />
      <path d={`M${centerX - 2.3 * u} ${u + bodyHeight - 2.2 * u}h${4.6 * u}`} />
      <path d={`M${bodyRight} ${cableY}c${1.6 * u} ${0.7 * u} ${1.8 * u} ${1.6 * u} ${1.8 * u} ${3.2 * u}V${cableBottom - 3.4 * u}c0 ${2.1 * u} ${0.9 * u} ${3.4 * u} ${2.8 * u} ${3.4 * u}s${3.1 * u} ${-1.3 * u} ${3.1 * u} ${-3.4 * u}V${plugY + 7 * u}`} />
      <g transform={`translate(${centerX} ${u + bodyHeight * 0.42}) scale(${u}) translate(-7.5 -9.3)`}>
        <path d="m8.6 5.4-3.3 4.5h2l-1 3.3 3.6-4.8H8Z" fill={strokeColor} stroke="none" />
      </g>
      <g transform={`translate(${plugX} ${plugY}) scale(${u}) translate(-18.4 -7.7)`} strokeWidth={strokeWidth / u}>
        <path d="m18.4 7.7-.5 3.7c1.2.5 1.7 1.2 1.7 2.1v1.2h2.3v-1.9c0-2.5-1.4-4.5-3.5-5.1ZM18.4 7.7l-1.2-.2-.3 2.5 1.2.3" />
      </g>
    </g>
  );
}

function EquipmentIcon({
  width, height, rotation = 0, PX, strokeWidth = 2,
  strokeColor = "#000000", fillColor = "#ffffff", kind,
}: C1_FurnitureRendererProps & { kind: "outdoor-heat-pump" | "wallbox" }) {
  const heatPump = kind === "outdoor-heat-pump";
  const symbolProps = {
    width, height, fillColor, strokeColor, strokeWidth,
    // Details have physical sizes; resizing extends the housing and cable runs.
    detailScale: heatPump ? 0.4 * PX / 15.5 : 0.5 * PX / 22,
  };
  return (
    <C1_FurnitureSvg
      PX={PX} width={width} height={height} rotation={rotation}
      viewBoxWidth={width} viewBoxHeight={height}
      originXPercentage={50} originYPercentage={heatPump ? -75 : 0}
    >
      {heatPump ? <OutdoorHeatPumpSymbol {...symbolProps} lamellaSpacing={0.05 * PX} /> : <WallboxSymbol {...symbolProps} />}
    </C1_FurnitureSvg>
  );
}

function EquipmentBox({
  width,
  height,
  rotation = 0,
  PX,
  strokeWidth = 2,
  strokeColor = "#000000",
  fillColor = "#ffffff",
  ownerRotation = 0,
  ownerReflect = false,
  kind,
}: C1_FurnitureRendererProps & { kind: "electrical-cabinet" | "network-cabinet" }) {
  const inset = Math.max(3, Math.min(width, height) * 0.08);
  return (
    <C1_FurnitureSvg
      PX={PX}
      width={width}
      height={height}
      rotation={rotation}
      viewBoxX={-width / 2}
      viewBoxY={-height / 2}
      viewBoxWidth={width}
      viewBoxHeight={height}
      originXPercentage={50}
      originYPercentage={0}
    >
      <rect
        x={-width / 2 + 2}
        y={-height / 2 + 2}
        width={width - 4}
        height={height - 4}
        rx={Math.min(6, height * 0.08)}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth * 2}
      />
      <C1_FurnitureUprightContent anchorX={0} anchorY={0} ownerRotation={Number(ownerRotation)} ownerReflect={Boolean(ownerReflect)}>
        {kind === "electrical-cabinet" ? (
          <g fill="none" stroke={strokeColor} strokeWidth={strokeWidth * 1.4}>
            <rect x={-width / 2 + inset} y={-height / 2 + inset} width={width - 2 * inset} height={height - 2 * inset} rx={2} />
            <path d={`M 0 ${-height / 2 + inset} V ${height / 2 - inset}`} />
            <circle cx={width * 0.08} cy={0} r={Math.max(1.5, strokeWidth)} fill={strokeColor} />
          </g>
        ) : (
          <g fill="none" stroke={strokeColor} strokeWidth={strokeWidth * 1.3}>
            <rect x={-width / 2 + inset} y={-height / 2 + inset} width={width - 2 * inset} height={height - 2 * inset} rx={2} />
            {[-0.22, 0, 0.22].map((offset) => (
              <path key={offset} d={`M ${-width * 0.28} ${offset * height} H ${width * 0.28}`} />
            ))}
            <circle cx={-width * 0.2} cy={height * 0.32} r={Math.max(1.3, strokeWidth * 0.7)} fill={strokeColor} />
            <circle cx={-width * 0.1} cy={height * 0.32} r={Math.max(1.3, strokeWidth * 0.7)} fill={strokeColor} />
          </g>
        )}
      </C1_FurnitureUprightContent>
    </C1_FurnitureSvg>
  );
}

export const OutdoorHeatPump: C1_FurnitureDefinition = {
  id: "outdoorHeatPump",
  hybridEquipment: true,
  description: { de: "Außeneinheit Wärmepumpe", en: "Outdoor heat-pump unit" },
  category: FurnitureCategory.Outdoor,
  defaultWidth: 0.9,
  defaultHeight: 1.2,
  originXPercentage: 50,
  // Bezugspunkt um 75 % der Gerätetiefe oberhalb der Oberkante.
  originYPercentage: -75,
  grabsW: true,
  grabsH: true,
  autoRotate: false,
  autoReflect: false,
  properties: [],
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <g transform="translate(1 5)"><OutdoorHeatPumpSymbol /></g>
    </svg>
  ),
  Renderer: (props) => <EquipmentIcon {...props} kind="outdoor-heat-pump" />,
};

export const ElectricalCabinet: C1_FurnitureDefinition = {
  id: "electricalCabinet",
  description: { de: "Schaltschrank", en: "Electrical cabinet" },
  category: RoomType.UtilityConnectionRoom,
  defaultWidth: 0.8,
  defaultHeight: 0.25,
  originXPercentage: 50,
  originYPercentage: 0,
  grabsW: true,
  grabsH: true,
  autoRotate: true,
  autoReflect: true,
  properties: [],
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeLinejoin="miter">
      <rect x="4" y="2" width="16" height="20" strokeWidth="1.6" />
      <path d="M6 7h4.5M6 12h4.5M6 17h4.5M13.5 7H18M13.5 12H18M13.5 17H18" strokeWidth=".8" />
      <g fill="currentColor" stroke="none">
        <rect x="6.7" y="5.5" width="1.5" height="3" />
        <rect x="8.7" y="5.5" width="1.2" height="3" />
        <rect x="7.5" y="15.5" width="1.8" height="3" />
        <rect x="14" y="10.5" width="1.5" height="3" />
        <rect x="16" y="10.5" width="1.2" height="3" />
        <rect x="15.5" y="15.5" width="1.5" height="3" />
      </g>
    </svg>
  ),
  Renderer: (props) => <EquipmentBox {...props} kind="electrical-cabinet" />,
};

export const NetworkCabinet: C1_FurnitureDefinition = {
  id: "networkCabinet",
  description: { de: "Netzwerkverteiler", en: "Network cabinet" },
  category: RoomType.UtilityConnectionRoom,
  defaultWidth: 0.6,
  defaultHeight: 0.2,
  originXPercentage: 50,
  originYPercentage: 0,
  grabsW: true,
  grabsH: true,
  autoRotate: true,
  autoReflect: true,
  properties: [],
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeLinejoin="miter">
      <rect x="3" y="3" width="18" height="18" strokeWidth="1.6" />
      <rect x="3" y="6" width="18" height="4" strokeWidth=".5" />
      {[5, 8, 11, 14, 17].map(x => (
        <rect key={x} x={x} y="7" width="2" height="2" strokeWidth=".4" />
      ))}
    </svg>
  ),
  Renderer: (props) => <EquipmentBox {...props} kind="network-cabinet" />,
};

export const Wallbox: C1_FurnitureDefinition = {
  id: "wallbox",
  hybridEquipment: true,
  description: { de: "Wallbox", en: "Wallbox" },
  category: FurnitureCategory.Outdoor,
  additionalCategories: [RoomType.Garage],
  defaultWidth: 0.5,
  defaultHeight: 0.7,
  originXPercentage: 50,
  originYPercentage: 0,
  grabsW: true,
  grabsH: true,
  autoRotate: true,
  autoReflect: true,
  properties: [],
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <g transform="translate(1 2)"><WallboxSymbol /></g>
    </svg>
  ),
  Renderer: (props) => <EquipmentIcon {...props} kind="wallbox" />,
};
